/* ** Case of one item by accordion ** */
import { useState } from 'react';

export type AccordionIndexes = Record<string, number[]>;

export type ExpandAccordion = {
  indexes: AccordionIndexes;
  expand: (id: string) => void;
  collapse: (id: string) => void;
  toggle: (id: string) => void;
  setIndex: (id: string, value: number[]) => void;
  isExpanded: (id: string) => boolean;
  isCollapsed: (id: string) => boolean;
};

/* ** Custom hook that treat a record of accordions of one item ** */
export function useExpandAccordion(defaultValues: AccordionIndexes): ExpandAccordion {
  const [indexes, setIndexes] = useState<AccordionIndexes>(defaultValues);

  /* ** Collapse check ** */
  const isCollapsed = (id: string): boolean => {
    return indexes[id].length === 0;
  };

  /* ** Expand check ** */
  const isExpanded = (id: string): boolean => {
    return indexes[id] && indexes[id].length > 0;
  };

  /* ** Expand accordion by id ** */
  const handleAccordionExpand = (id: string): void => {
    setIndexes((prevIndexes) => ({ ...prevIndexes, [id]: [0] }));
  };

  /* ** Collapse accordion by id ** */
  const handleAccordionCollapse = (id: string): void => {
    setIndexes((prevIndexes) => ({ ...prevIndexes, [id]: [] }));
  };

  /* ** Toggle accordion by id ** */
  const handleAccordionToggle = (id: string): void => {
    if (isExpanded(id)) {
      handleAccordionCollapse(id);
    } else {
      handleAccordionExpand(id);
    }
  };

  /* ** Set accordion value ** */
  const handleAccordionValue = (id: string, value: number[]): void => {
    setIndexes((prevIndexes) => ({ ...prevIndexes, [id]: value }));
  };

  return {
    indexes,
    expand: handleAccordionExpand,
    collapse: handleAccordionCollapse,
    toggle: handleAccordionToggle,
    setIndex: handleAccordionValue,
    isCollapsed,
    isExpanded,
  };
}
